import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import StatusPill, { CASE_STATUS_PILL, type PillVariant } from '../components/StatusPill';
import { ArrowRight, Chevron, Cross, Plus, Tick } from '../components/AppIcons';
import { Brand, brandify } from '../../components/Brand';
import { useActiveCompany } from '../state';
import { createCase, type CaseKind } from '../../lib/entities/cases';
import { getEntityQuestions, answerEntityQuestion } from '../../lib/entities/entityQuestions';
import type { OnboardingQuestion } from '../../lib/entities/onboarding';
import PredicateQuestionLoop from '../components/PredicateQuestionLoop';
import type { CaseRisk, CaseStatus, HrCase } from '../types';
import '../../styles/app-cases.css';
import '../../styles/app-employees.css'; // .emp-caselink on the person line, .emp-modal__* on the question loop

/* ---------------------------------------------------- Shared case helpers */

/** Risk as a pill (detail page header); the list rows use dots instead. */
export const RISK_PILL: Record<CaseRisk, { variant: PillVariant; label: string }> = {
  low: { variant: 'ok', label: 'Low risk' },
  medium: { variant: 'warn', label: 'Medium risk' },
  high: { variant: 'danger', label: 'High risk' },
};

/* ------------------------------------------------------------- List page */

/**
 * The case kinds offered in the "start a case" form. Each carries the backend
 * enum value we POST and a friendly label for the picker; the display list uses
 * the mapper in entities/cases, so a saved case may read back slightly shorter
 * (e.g. "Sickness absence" → "Absence").
 */
const CASE_KINDS: { value: CaseKind; label: string }[] = [
  { value: 'grievance', label: 'Grievance' },
  { value: 'disciplinary', label: 'Disciplinary' },
  { value: 'sicknessAbsence', label: 'Sickness absence' },
  { value: 'leaverOffboarding', label: 'Leaver & offboarding' },
  { value: 'flexibleWorking', label: 'Flexible working' },
  { value: 'probation', label: 'Probation' },
  { value: 'starterOnboarding', label: 'Starter & onboarding' },
  { value: 'other', label: 'Something else' },
];

const FILTERS: { key: CaseStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'In progress' },
  { key: 'waiting', label: 'Waiting' },
  { key: 'escalated', label: 'With expert' },
  { key: 'resolved', label: 'Resolved' },
];

/** One line under each row: the next safe step, or how it ended. */
export function previewLine(c: HrCase): string {
  if (c.status === 'resolved') {
    const last = c.steps[c.steps.length - 1]?.detail;
    return last ? `Resolved: ${last.charAt(0).toLowerCase()}${last.slice(1)}` : 'Resolved and kept on file.';
  }
  return c.nextStep ?? 'reeve is preparing your next safe step.';
}

function RiskDot({ risk }: { risk: CaseRisk }) {
  return (
    <span className={`case-risk case-risk--${risk}`}>
      <span className="case-risk__dot" />
      {RISK_PILL[risk].label}
    </span>
  );
}

function CaseRow({ hrCase, isNew }: { hrCase: HrCase; isNew: boolean }) {
  const navigate = useNavigate();
  const status = CASE_STATUS_PILL[hrCase.status];
  const resolved = hrCase.status === 'resolved';
  return (
    <Link
      to={`/app/cases/${hrCase.id}`}
      className={`rowlist__row case-row${resolved ? ' case-row--resolved' : ''}${isNew ? ' case-row--new' : ''}`}
    >
      <span className="case-row__main">
        <span className="case-row__top">
          <span className="case-row__title">{hrCase.title}</span>
          <span className="case-kind">{hrCase.kind}</span>
        </span>
        <span className="case-row__meta">
          {/* The row is a Link, so the person link is a button navigating on its own. */}
          {hrCase.person &&
            (hrCase.employeeId ? (
              <button
                type="button"
                className="emp-caselink"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/app/employees/${hrCase.employeeId}`);
                }}
              >
                {hrCase.person}
              </button>
            ) : (
              hrCase.person
            ))}
          {hrCase.person ? ' · ' : ''}
          {hrCase.opened}
        </span>
        <span className="case-row__next">
          {!resolved && <ArrowRight size={13} />}
          <span>{brandify(previewLine(hrCase))}</span>
        </span>
      </span>
      <span className="case-row__side">
        <RiskDot risk={hrCase.risk} />
        <StatusPill variant={status.variant}>{status.label}</StatusPill>
        <Chevron size={16} className="case-row__chev" />
      </span>
    </Link>
  );
}

export default function CasesPage() {
  const { companyId, cases, companyLoading, refetchCases } = useActiveCompany();
  const [filter, setFilter] = useState<CaseStatus | 'all'>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [newId, setNewId] = useState<string | null>(null);

  const [kind, setKind] = useState<CaseKind>(CASE_KINDS[0].value);
  const [who, setWho] = useState('');
  const [what, setWhat] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // After create, the modal transitions in-place to the per-case predicate question loop (if any apply).
  const [phase, setPhase] = useState<'form' | 'questions'>('form');
  const [created, setCreated] = useState<{ id: string } | null>(null);
  const [firstQuestion, setFirstQuestion] = useState<OnboardingQuestion | null>(null);

  const toastTimer = useRef<number>();
  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  const closeRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [modalOpen]);

  const visible = filter === 'all' ? cases : cases.filter((c) => c.status === filter);
  const countFor = (key: CaseStatus | 'all') =>
    key === 'all' ? cases.length : cases.filter((c) => c.status === key).length;

  const openModal = () => {
    setKind(CASE_KINDS[0].value);
    setWho('');
    setWhat('');
    setFormError(null);
    setPhase('form');
    setCreated(null);
    setFirstQuestion(null);
    setModalOpen(true);
  };

  // Finish: refresh, flag the new row, toast, close. Called after the questions (or immediately when there
  // are none, or if the user closes mid-loop — the case already exists either way).
  const finish = async (id: string) => {
    await refetchCases();
    setNewId(id);
    setFilter('all');
    setModalOpen(false);
    setToastVisible(true);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToastVisible(false), 5000);
  };

  // Dismissal is allowed ONLY on the form (nothing created yet). Once the per-case questions are showing,
  // the case exists and every question MUST be answered yes/no — an unanswered predicate resolves as false
  // and shows the wrong tier — so there is no cancel, Escape, or backdrop close out of them.
  const closeModal = () => {
    if (phase === 'questions') return;
    setModalOpen(false);
  };
  closeRef.current = closeModal;

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!companyId || submitting) return;
    const person = who.trim();
    const shortName = person.split('·')[0].trim();
    const label = CASE_KINDS.find((k) => k.value === kind)?.label ?? 'Case';
    setSubmitting(true);
    setFormError(null);
    try {
      const createdCase = await createCase({
        companyId,
        title: shortName ? `${label}: ${shortName}` : label,
        kind,
        personLabel: person || undefined,
        summary: what.trim() || undefined,
      });
      // Any per-case questions to ask? Pre-fetch so the modal only transitions when there's one to show.
      const next = await getEntityQuestions('/api/cases', createdCase.id);
      if (!next.done && next.question) {
        setCreated({ id: createdCase.id });
        setFirstQuestion(next.question);
        setPhase('questions');
      } else {
        await finish(createdCase.id);
      }
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : 'We couldn’t open the case. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="apage">
      <PageHeader
        eyebrow="Cases"
        title="Your cases"
        lead="When something comes up, like a resignation, a grievance or someone off sick, this is where you handle it."
        actions={
          <button type="button" className="btn btn-primary btn-sm" onClick={openModal}>
            <Plus size={16} />
            Start a case
          </button>
        }
      />

      {toastVisible && (
        <div className="case-toast" role="status">
          <span className="case-toast__icon">
            <Tick size={12} color="#fff" />
          </span>
          <span>
            Case opened. <Brand /> is preparing your next safe step. It’s at the top of your list.
          </span>
        </div>
      )}

      <div className="case-filters" role="group" aria-label="Filter cases">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`case-filter${filter === f.key ? ' is-active' : ''}`}
            aria-pressed={filter === f.key}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            <span className="case-filter__count">{countFor(f.key)}</span>
          </button>
        ))}
      </div>

      <div className="acard case-list">
        {companyLoading && cases.length === 0 ? (
          <div className="empty">Loading your cases…</div>
        ) : visible.length === 0 ? (
          <div className="empty">Nothing here right now, which usually means good news.</div>
        ) : (
          <div className="rowlist">
            {visible.map((c) => (
              <CaseRow key={c.id} hrCase={c} isNew={c.id === newId} />
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="modal-scrim" onClick={closeModal} role="presentation">
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="case-start-title"
            onClick={(e) => e.stopPropagation()}
          >
            {/* No close control during the questions phase: the answers are required, not optional. */}
            {phase === 'form' && (
              <button type="button" className="modal__close" aria-label="Close" onClick={closeModal}>
                <Cross size={18} />
              </button>
            )}
            {phase === 'form' ? (
              <>
                <h2 className="modal__title" id="case-start-title">
                  Start a case
                </h2>
                <p className="case-modal__sub">
                  Tell <Brand /> what’s happening, in your own words. It stays private, and you’ll get a
                  safe next step, not a lecture.
                </p>
                <form className="case-form" onSubmit={handleCreate}>
              <label className="field">
                <span className="field__label">What kind of situation is it?</span>
                <select
                  className="select"
                  value={kind}
                  onChange={(e) => setKind(e.target.value as CaseKind)}
                >
                  {CASE_KINDS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="field__label">Who’s involved?</span>
                <input
                  className="input"
                  placeholder="e.g. Alex Morgan · Front of house"
                  value={who}
                  onChange={(e) => setWho(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field__label">What’s happening?</span>
                <textarea
                  className="textarea"
                  placeholder="A sentence or two is plenty. You can add more later."
                  value={what}
                  onChange={(e) => setWhat(e.target.value)}
                />
              </label>
              {formError && (
                <p className="case-modal__sub" role="alert">
                  {formError}
                </p>
              )}
              <div className="case-modal__foot">
                <button type="button" className="btn btn-secondary btn-xs" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-xs" disabled={submitting}>
                  {submitting ? 'Opening…' : 'Open the case'}
                </button>
              </div>
                </form>
              </>
            ) : (
              created &&
              firstQuestion && (
                <PredicateQuestionLoop
                  lead={
                    <>The case is open. These answers set which requirements apply, so each needs a yes or no.</>
                  }
                  initialQuestion={firstQuestion}
                  submitAnswer={(key, value) =>
                    answerEntityQuestion('/api/cases', created.id, key, value)
                  }
                  onDone={() => finish(created.id)}
                />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
